import type { LLMModel } from "@/hooks/api/llm"
import type { FileBadgeData } from "@/types"

const AUDIO_INPUT_MODALITY = "audio"

const hasAudioModality = (model: LLMModel | undefined): boolean =>
  !!model?.inputModalities?.some(
    (modality) =>
      typeof modality === "string" &&
      modality.trim().toLowerCase() === AUDIO_INPUT_MODALITY
  )

export const isAudioAttachment = (
  attachment?: Pick<FileBadgeData, "type"> | null
): boolean => {
  const contentType = attachment?.type?.trim().toLowerCase()
  return !!contentType && contentType.startsWith("audio/")
}

export const attachmentsIncludeAudio = (
  attachments?: Array<Pick<FileBadgeData, "type"> | null | undefined>
): boolean => {
  return (attachments || []).some((attachment) => isAudioAttachment(attachment))
}

export const getSystemDefaultTextModelId = (
  sysConfig: unknown,
  availableModels: LLMModel[]
): string => {
  const config = (sysConfig ?? {}) as Record<string, unknown>
  const configDefault =
    (config["defaultLLMModel"] as string | undefined) ??
    (config["llmDefaultModel"] as string | undefined) ??
    (config["LLM_DEFAULT_MODEL"] as string | undefined)
  return configDefault || availableModels[0]?.modelId || ""
}

export const modelSupportsAudioInput = (
  modelId: string | null | undefined,
  availableModels: LLMModel[]
): boolean => {
  if (!modelId) return false
  return hasAudioModality(
    availableModels.find((candidate) => candidate.modelId === modelId)
  )
}

export const getAudioCapableModels = (
  availableModels: LLMModel[]
): LLMModel[] => availableModels.filter(hasAudioModality)

export const getModelDisplayName = (
  modelId: string | null | undefined,
  availableModels: LLMModel[]
): string => {
  if (!modelId) return ""
  return (
    availableModels.find((candidate) => candidate.modelId === modelId)
      ?.displayName || modelId
  )
}

export const getVoiceRecordingDisabledReason = ({
  resolvedModelId,
  availableModels,
  unavailableReason,
  unsupportedReason,
}: {
  resolvedModelId: string | null | undefined
  availableModels: LLMModel[]
  unavailableReason: string
  unsupportedReason: (modelDisplayName: string) => string
}): string | undefined => {
  if (!resolvedModelId) return unavailableReason

  const model = availableModels.find(
    (candidate) => candidate.modelId === resolvedModelId
  )
  if (!model) return unavailableReason
  if (hasAudioModality(model)) return undefined

  return unsupportedReason(
    getModelDisplayName(resolvedModelId, availableModels)
  )
}

/**
 * Validate audio/model compatibility when audio attachments are present.
 * Defers to backend when the resolved model isn't in the local list (e.g.
 * inactive model or unloaded store) so we don't block valid requests that
 * the backend would otherwise resolve via its own fallback chain.
 */
export const validateAudioModel = (
  attachments:
    | Array<Pick<FileBadgeData, "type"> | null | undefined>
    | undefined,
  resolvedModelId: string | null | undefined,
  availableModels: LLMModel[]
): { title: string; description: string } | null => {
  if (!attachmentsIncludeAudio(attachments)) return null
  if (!resolvedModelId) return null
  const model = availableModels.find(
    (candidate) => candidate.modelId === resolvedModelId
  )
  if (!model) return null
  if (hasAudioModality(model)) return null
  return {
    title: "common.audioInput.error.title",
    description: "common.audioInput.unsupportedModel",
  }
}
